// Implements: F-UIX-018, F-UIX-015
/**
 * The discreet 80 % notice of the conversation view (UIX.1). Pure presentation.
 *
 * It names which limit is nearly reached (the person's allowance, the team's budget
 * or the organization's budget) and links to "Mi consumo". It shows no money, count,
 * limit, percentage or date (OD-12): those live only in the backoffice. It is a
 * polite, non-modal status line that takes no focus and blocks nothing.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import styles from "./UsageNotice.module.css";

/** The limits a warning can name: the three `RefusalReason` values a warning uses (D4). */
export type WarnedLimit = "person" | "team" | "organization";

/**
 * Where "Mi consumo" lives: the backoffice section S12c-1 mounts. Until that route exists
 * the link lands on the not-found page; the address is fixed here so both slices agree.
 */
export const MY_USAGE_HREF = "/backoffice/usage";

const SENTENCES = {
  person: "usage.notice.person",
  team: "usage.notice.team",
  organization: "usage.notice.organization",
} as const satisfies Record<WarnedLimit, string>;

export interface UsageNoticeProps {
  readonly limit: WarnedLimit;
}

export function UsageNotice({ limit }: UsageNoticeProps): ReactElement {
  const { t } = useTranslation();
  return (
    <p className={styles.notice} role="status" aria-label={t("usage.notice.label")}>
      {t(SENTENCES[limit])}{" "}
      <Link className={styles.link} to={MY_USAGE_HREF}>
        {t("usage.notice.link")}
      </Link>
    </p>
  );
}
